/**
 * apps/battle/client/src/terminal/config.ts
 * Phase 4.0 Terminal Mode — environment + feature flags.
 *
 * TERMINAL_DEV_MODE gates the [R] (Review portal) and [Q] (quit to /) keys.
 * Defaults true so local dev keeps the escape hatches; closed-beta build
 * sets VITE_TERMINAL_DEV_MODE=false at build time.
 *
 * MAX_ATTEMPTS mirrors the server constant; UI never trusts it for grading,
 * only for rendering attempt-dot count when an in-flight response is absent.
 */
const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env ?? {};

export const TERMINAL_DEV_MODE: boolean =
  (env.VITE_TERMINAL_DEV_MODE ?? 'true').toString().toLowerCase() !== 'false';

/**
 * URL the `[R] review portal` key opens (dev-mode only).
 *
 * The review app (@econquest/review) runs as a SEPARATE process on its own port:
 *   - dev:  http://localhost:5174  (its Vite dev server)
 *   - prod: wherever it's deployed (e.g. https://review.econquest.app)
 *
 * Override at build/dev time with VITE_REVIEW_URL=... — defaults to the Vite dev URL.
 * The previous default of '/review' incorrectly resolved against the battle origin
 * and hit the Fastify SPA fallback (rendering the graph again, not the review UI).
 */
export const REVIEW_URL: string =
  env.VITE_REVIEW_URL ?? 'http://localhost:5174';

/** Default attempts per battle question. Mirrors server MAX_ATTEMPTS_PER_BATTLE. */
export const MAX_ATTEMPTS_DEFAULT = 3;

/** Auto-advance timeout on narrative screens, ms. Spec §11 Q2 → 8 s. */
export const NARRATIVE_AUTO_ADVANCE_MS = 8000;

/**
 * M10 — Badge display metadata. Maps the server BADGE_CODE enum to a
 * spelled-out name + short description for the leaderboard "MY BADGES" panel
 * and the in-battle "★ Badge unlocked" toast. Renderers should fall back to
 * the raw code (with underscores → spaces) when a code is missing here.
 */
export interface BadgeMeta {
  name: string;
  description: string;
}

export const BADGE_METADATA: Record<string, BadgeMeta> = {
  // Progression
  FIRST_CLEAR: { name: 'First Trade', description: 'Clear your first question.' },
  FIRST_CLUSTER: { name: 'Beachhead', description: 'Clear your first cluster.' },
  FIVE_CLUSTERS: { name: 'Market Mover', description: 'Clear 5 clusters.' },
  EXAM_READY: { name: 'Exam Ready', description: 'Clear every World 1 cluster.' },
  BOTH_WORLDS: { name: 'Globalist', description: 'Clear at least one cluster in each world.' },
  THE_ECONOMIST: { name: 'The Economist', description: '100% map completion — every cluster, both worlds.' },
  FORTY_TWO: { name: 'The Answer', description: 'Clear 42 distinct questions.' },
  // Levels
  LEVEL_3: { name: 'Apprentice', description: 'Reach level 3.' },
  LEVEL_5: { name: 'Practitioner', description: 'Reach level 5.' },
  LEVEL_10: { name: 'Specialist', description: 'Reach level 10.' },
  LEVEL_15: { name: 'Grandmaster', description: 'Reach level 15.' },
  // Streaks
  STREAK_3: { name: 'Habit Forming', description: 'Maintain a 3-day streak.' },
  STREAK_7: { name: 'Week Streak', description: 'Maintain a 7-day streak.' },
  STREAK_14: { name: 'Unstoppable', description: 'Maintain a 14-day streak.' },
  // Mastery
  FIRST_STRIKE: { name: 'Bullseye', description: 'Land your first perfect strike.' },
  STRIKE_10: { name: 'Sharpshooter', description: 'Land 10 perfect strikes.' },
  CHAIN_5: { name: 'Domino Effect', description: 'Trigger 5 chain reactions.' },
  HEAVY_LIFTER: { name: 'Heavy Lifter', description: 'Clear a 12-mark question.' },
  DONT_PANIC: { name: "Don't Panic", description: 'Clear a question on your final attempt.' },
  // Portals
  PORTAL_FIRST: { name: 'Hyperspace Jump', description: 'Deploy your first portal.' },
  PORTAL_ALL_PERM: { name: 'Infinite Improbability', description: 'Deploy all permanent portals.' },
  PORTAL_10: { name: 'Frequent Flyer', description: 'Deploy portals 10 times.' },
  // Persistence
  PRACTICE_10: { name: 'Drill Sergeant', description: 'Clear 10 questions in practice mode.' },
  RESILIENT: { name: 'Resilient', description: 'Accumulate 25 retries — persistence pays.' },
  TOP_PERCENTILE: { name: 'Top of the Class', description: 'Reach the top 10% of the leaderboard.' },
};

/** All badge codes in display order (for the locked/unlocked MY BADGES panel). */
export const BADGE_CODES_ORDERED: string[] = Object.keys(BADGE_METADATA);

/** Display name for a badge code, falling back to a humanised code. */
export function badgeName(code: string): string {
  return BADGE_METADATA[code]?.name ?? code.replace(/_/g, ' ');
}

/** Short description for a badge code, or an empty string when unknown. */
export function badgeDescription(code: string): string {
  return BADGE_METADATA[code]?.description ?? '';
}
